import {
    AuthenticationDetails,
    CognitoUser,
    CognitoUserAttribute,
    CognitoUserPool,
} from "amazon-cognito-identity-js";
import QRCode from "qrcode";
import { ADMIN_ROLE, STANDARD_ROLE } from "global/constants";

const ISSUER = "TSL";

let pendingUser = null;
let pendingEmail = "";
let pendingPassword = "";

export function cognitoConfigured() {
    return Boolean(
        process.env.REACT_APP_COGNITO_USER_POOL_ID
        && process.env.REACT_APP_COGNITO_CLIENT_ID
    );
}

function getPoolConfig() {
    const UserPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
    const ClientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    if (!UserPoolId || !ClientId) {
        throw new Error("auth.error.notConfigured");
    }
    return { UserPoolId, ClientId };
}

function createPool() {
    return new CognitoUserPool(getPoolConfig());
}

function userFor(email) {
    return new CognitoUser({
        Username: email,
        Pool: createPool(),
    });
}

export function authErrorMessage(error) {
    const code = error?.code || error?.name || error?.__type;
    const message = error?.message || "auth.error.generic";
    if (message.includes("SECRET_HASH")) {
        return "auth.error.secretHash";
    }
    if (code === "NotAuthorizedException" || code === "UserNotFoundException") {
        return "auth.error.incorrectCredentials";
    }
    if (code === "UserNotConfirmedException") {
        return "auth.error.confirmEmail";
    }
    if (code === "CodeMismatchException" || code === "EnableSoftwareTokenMFAException") {
        return "auth.error.invalidCode";
    }
    if (code === "ExpiredCodeException") {
        return "auth.error.expiredCode";
    }
    if (code === "UsernameExistsException") {
        return "auth.error.emailExists";
    }
    if (code === "LimitExceededException" || code === "TooManyRequestsException") {
        return "auth.error.tooManyAttempts";
    }
    if (message.includes("does not have SMS configuration") || message.includes("Unable to send the message")) {
        return "auth.error.smsPool";
    }
    return message;
}

export function mfaMethodsFromChallenge(challengeParameters = {}, { setup = false } = {}) {
    const raw = String(
        challengeParameters.MFAS_CAN_SETUP
        || challengeParameters.MFAS_CAN_CHOOSE
        || ""
    );
    const found = [];
    if (raw.includes("SMS_MFA")) {
        found.push("SMS");
    }
    if (raw.includes("SOFTWARE_TOKEN_MFA")) {
        found.push("TOTP");
    }
    if (setup || found.length === 0) {
        return ["SMS", "TOTP"];
    }
    return found;
}

function challengeResult(step, extra = {}) {
    return { step, ...extra };
}

function bindCallbacks(resolve, reject) {
    return {
        onSuccess: (session) => resolve(challengeResult("DONE", { session })),
        onFailure: reject,
        mfaRequired: (_challengeName, challengeParameters) => {
            resolve(challengeResult("SMS_CODE", {
                destination: challengeParameters?.CODE_DELIVERY_DESTINATION || "",
            }));
        },
        totpRequired: () => resolve(challengeResult("TOTP_CODE")),
        selectMFAType: (_challengeName, challengeParameters) => {
            resolve(challengeResult("SELECT_MFA", {
                methods: mfaMethodsFromChallenge(challengeParameters),
            }));
        },
        mfaSetup: (_challengeName, challengeParameters) => {
            resolve(challengeResult("SETUP_MFA", {
                methods: mfaMethodsFromChallenge(challengeParameters, { setup: true }),
            }));
        },
        newPasswordRequired: () => resolve(challengeResult("NEW_PASSWORD")),
    };
}

function authenticate(email, password) {
    pendingEmail = email;
    pendingPassword = password;
    const user = userFor(email);
    pendingUser = user;
    const details = new AuthenticationDetails({
        Username: email,
        Password: password,
    });

    return new Promise((resolve, reject) => {
        user.authenticateUser(details, bindCallbacks(resolve, reject));
    });
}

export function signIn(email, password) {
    return authenticate(email.trim(), password);
}

export function continuePendingSignIn() {
    if (!pendingEmail || !pendingPassword) {
        return Promise.reject(new Error("Sign in with your email and password."));
    }
    return authenticate(pendingEmail, pendingPassword);
}

export function releaseCredentials() {
    pendingPassword = "";
}

export function signUp({ email, password, phone }) {
    const username = email.trim();
    const attributes = [
        new CognitoUserAttribute({ Name: "email", Value: username }),
    ];
    if (phone) {
        attributes.push(new CognitoUserAttribute({
            Name: "phone_number",
            Value: phone.trim(),
        }));
    }

    return new Promise((resolve, reject) => {
        createPool().signUp(username, password, attributes, null, (err, result) => {
            if (err) {
                reject(err);
                return;
            }
            pendingUser = result.user;
            pendingEmail = username;
            pendingPassword = password;
            resolve({
                step: result.userConfirmed ? "CONFIRMED" : "CONFIRM_EMAIL",
            });
        });
    });
}

export function confirmEmail(code) {
    const user = pendingUser || userFor(pendingEmail);
    return new Promise((resolve, reject) => {
        user.confirmRegistration(code.trim(), true, (err) => {
            if (err) {
                reject(err);
                return;
            }
            resolve({ step: "CONFIRMED" });
        });
    });
}

export function resendEmailCode() {
    const user = pendingUser || userFor(pendingEmail);
    return new Promise((resolve, reject) => {
        user.resendConfirmationCode((err) => {
            if (err) {
                reject(err);
                return;
            }
            resolve();
        });
    });
}

function requireUser() {
    if (!pendingUser) {
        throw new Error("Sign in again to continue.");
    }
    return pendingUser;
}

export function submitMfaCode(code, method) {
    const mfaType = method === "TOTP" ? "SOFTWARE_TOKEN_MFA" : "SMS_MFA";
    return new Promise((resolve, reject) => {
        requireUser().sendMFACode(code.trim(), bindCallbacks(resolve, reject), mfaType);
    });
}

export function selectMfa(method) {
    const answer = method === "TOTP" ? "SOFTWARE_TOKEN_MFA" : "SMS_MFA";
    return new Promise((resolve, reject) => {
        requireUser().sendMFASelectionAnswer(answer, bindCallbacks(resolve, reject));
    });
}

export function beginTotpSetup(accountName) {
    const user = requireUser();
    return new Promise((resolve, reject) => {
        user.associateSoftwareToken({
            associateSecretCode: async (secret) => {
                try {
                    const label = encodeURIComponent(accountName || pendingEmail || "user");
                    const uri = `otpauth://totp/${ISSUER}:${label}?secret=${secret}&issuer=${ISSUER}`;
                    const qrCode = await QRCode.toDataURL(uri);
                    resolve({ secret, qrCode });
                } catch (err) {
                    reject(err);
                }
            },
            onFailure: reject,
        });
    });
}

function preferMfa(method) {
    const sms = {
        Enabled: method === "SMS",
        PreferredMfa: method === "SMS",
    };
    const totp = {
        Enabled: method === "TOTP",
        PreferredMfa: method === "TOTP",
    };
    return new Promise((resolve, reject) => {
        requireUser().setUserMfaPreference(sms, totp, (err) => {
            if (err) {
                reject(err);
                return;
            }
            resolve();
        });
    });
}

function persistSession() {
    const user = requireUser();
    if (user.signInUserSession?.isValid()) {
        user.cacheTokens();
    }
    return user.signInUserSession;
}

export function confirmTotpSetup(code) {
    const user = requireUser();
    const alreadySignedIn = Boolean(user.signInUserSession?.isValid());
    return new Promise((resolve, reject) => {
        user.verifySoftwareToken(code.trim(), ISSUER, {
            onSuccess: (session) => {
                if (!alreadySignedIn) {
                    resolve(challengeResult("DONE", { session }));
                    return;
                }
                preferMfa("TOTP").then(() => {
                    resolve(challengeResult("DONE", { session: persistSession() }));
                }).catch(reject);
            },
            onFailure: reject,
        });
    });
}

export function mfaIsConfigured() {
    return new Promise((resolve, reject) => {
        requireUser().getUserData((err, data) => {
            if (err) {
                reject(err);
                return;
            }
            const settings = data?.UserMFASettingList || [];
            resolve(Array.isArray(settings) && settings.length > 0);
        }, { bypassCache: true });
    });
}

export function clearCachedSignIn() {
    const user = requireUser();
    user.clearCachedUser();
}

export function keepCachedSignIn() {
    persistSession();
}

export function startSmsMfaSetup(phone) {
    const user = requireUser();
    const attributes = [
        new CognitoUserAttribute({ Name: "phone_number", Value: phone.trim() }),
    ];
    return new Promise((resolve, reject) => {
        user.updateAttributes(attributes, (err, _status, result) => {
            if (err) {
                reject(err);
                return;
            }
            const delivery = result?.CodeDeliveryDetailsList?.[0];
            if (delivery) {
                resolve({ destination: delivery.Destination || "" });
                return;
            }
            user.getAttributeVerificationCode("phone_number", {
                onFailure: reject,
                inputVerificationCode: (data) => resolve({
                    destination: data?.CodeDeliveryDetails?.Destination || "",
                }),
            });
        });
    });
}

export function resendPhoneVerification() {
    return new Promise((resolve, reject) => {
        requireUser().getAttributeVerificationCode("phone_number", {
            onFailure: reject,
            inputVerificationCode: (data) => resolve({
                destination: data?.CodeDeliveryDetails?.Destination || "",
            }),
        });
    });
}

export function confirmSmsMfaSetup(code) {
    const user = requireUser();
    return new Promise((resolve, reject) => {
        user.verifyAttribute("phone_number", code.trim(), {
            onFailure: reject,
            onSuccess: () => {
                preferMfa("SMS").then(() => {
                    resolve(challengeResult("DONE", { session: persistSession() }));
                }).catch(reject);
            },
        });
    });
}

export function completeNewPassword(newPassword) {
    return new Promise((resolve, reject) => {
        requireUser().completeNewPasswordChallenge(newPassword, {}, bindCallbacks(
            (result) => {
                pendingPassword = newPassword;
                resolve(result);
            },
            reject,
        ));
    });
}

export function requestPasswordReset(email) {
    pendingEmail = email.trim();
    pendingUser = userFor(pendingEmail);
    return new Promise((resolve, reject) => {
        pendingUser.forgotPassword({
            onSuccess: (data) => resolve({
                step: "RESET_CODE",
                destination: data?.CodeDeliveryDetails?.Destination || "",
            }),
            onFailure: reject,
            inputVerificationCode: (data) => resolve({
                step: "RESET_CODE",
                destination: data?.CodeDeliveryDetails?.Destination || "",
            }),
        });
    });
}

export function confirmPasswordReset(code, newPassword) {
    const user = pendingUser || userFor(pendingEmail);
    return new Promise((resolve, reject) => {
        user.confirmPassword(code.trim(), newPassword, {
            onSuccess: () => resolve({ step: "SIGN_IN" }),
            onFailure: reject,
        });
    });
}

export function restoreSession() {
    let user;
    try {
        user = createPool().getCurrentUser();
    } catch (err) {
        return Promise.resolve(null);
    }
    if (!user) {
        return Promise.resolve(null);
    }
    pendingUser = user;
    return new Promise((resolve) => {
        user.getSession((err, session) => {
            if (err || !session?.isValid()) {
                resolve(null);
                return;
            }
            resolve(session);
        });
    });
}

const tokenListeners = new Set();
let publishedToken = undefined;
let refreshInFlight = null;

function tokenExpiry(token) {
    const part = String(token || "").split(".")[1];
    if (!part) {
        return 0;
    }
    try {
        const padded = part
            .replace(/-/g, "+")
            .replace(/_/g, "/")
            .padEnd(Math.ceil(part.length / 4) * 4, "=");
        const payload = JSON.parse(atob(padded));
        return typeof payload.exp === "number" ? payload.exp : 0;
    } catch (err) {
        return 0;
    }
}

export function tokenExpiryMs(token) {
    return tokenExpiry(token) * 1000;
}

export function accessTokenExpiresSoon(token, skewSeconds = 60) {
    if (!token) {
        return true;
    }
    return tokenExpiry(token) * 1000 <= Date.now() + skewSeconds * 1000;
}

function publishAccessToken(token) {
    if (token === publishedToken) {
        return;
    }
    publishedToken = token;
    tokenListeners.forEach((listener) => listener(token));
}

export function subscribeAccessToken(listener) {
    tokenListeners.add(listener);
    return () => tokenListeners.delete(listener);
}

export function refreshAccessToken() {
    if (!refreshInFlight) {
        refreshInFlight = restoreSession()
            .then((session) => {
                const next = session ? session.getAccessToken().getJwtToken() : null;
                publishAccessToken(next);
                return next;
            })
            .finally(() => {
                refreshInFlight = null;
            });
    }
    return refreshInFlight;
}

export async function ensureAccessToken(token) {
    if (token && !accessTokenExpiresSoon(token)) {
        return token;
    }
    return refreshAccessToken();
}

const PASSWORD_RESET_KEY = "tslPasswordReset";

export function queuePasswordReset(email) {
    sessionStorage.setItem(PASSWORD_RESET_KEY, email || "");
}

export function takePasswordReset() {
    const email = sessionStorage.getItem(PASSWORD_RESET_KEY);
    if (email === null) {
        return null;
    }
    sessionStorage.removeItem(PASSWORD_RESET_KEY);
    return email;
}

export function signOut() {
    let user = pendingUser;
    pendingUser = null;
    pendingEmail = "";
    pendingPassword = "";
    if (!user) {
        try {
            user = createPool().getCurrentUser();
        } catch (err) {
            user = null;
        }
    }
    if (user) {
        user.signOut();
    }
}

export function sessionIdentity(session) {
    const payload = session.getIdToken().decodePayload();
    const groups = Array.isArray(payload["cognito:groups"]) ? payload["cognito:groups"] : [];
    return {
        accessToken: session.getAccessToken().getJwtToken(),
        id: payload.sub || "",
        email: payload.email || "",
        role: groups.includes(ADMIN_ROLE) ? ADMIN_ROLE : STANDARD_ROLE,
    };
}

export function changePassword(oldPassword, newPassword) {
    let user;
    try {
        user = createPool().getCurrentUser();
    } catch (err) {
        return Promise.reject(err);
    }
    if (!user) {
        return Promise.reject(new Error("Sign in again to change your password."));
    }
    return new Promise((resolve, reject) => {
        user.getSession((err) => {
            if (err) {
                reject(err);
                return;
            }
            user.changePassword(oldPassword, newPassword, (changeErr, result) => {
                if (changeErr) {
                    reject(changeErr);
                    return;
                }
                resolve(result);
            });
        });
    });
}
